/**
 * Escanear desde la PC con el teléfono (2026-11-02). Ver `lib/telefonoDeLaPc.ts`.
 *
 * Es lo que abre el botón del teléfono de cada casilla en la computadora, en
 * lugar de la cámara. Dos caras:
 *
 *   · **Sin teléfono unido**: una ventana con el QR. Se escanea con la cámara
 *     del teléfono, se abre la página del escáner, y la ventana pasa sola a la
 *     otra cara en cuanto el teléfono llega.
 *   · **Con el teléfono unido**: una tarjeta pequeña abajo a la izquierda, SIN
 *     tapar la pantalla. En la Caja se sigue viendo el carrito mientras se
 *     pasan los productos; en una ficha, la casilla que se va a llenar.
 *
 * ⚠️ La tarjeta va abajo a la IZQUIERDA a propósito: abajo a la derecha están
 * los botones de guardar de las ventanas y el de cobrar de la Caja.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Loader2, Smartphone, X } from "lucide-react";

import { Modal } from "@/components/Modal";
import { Boton } from "@/components/ui";
import { mensajeDeError } from "@/lib/api";
import {
  capturarDelTelefono,
  direccionDelQr,
  pedirQr,
  soltarTelefono,
  useTelefono,
} from "@/lib/telefonoDeLaPc";
import { aviso } from "@/stores/ui";

type Props = {
  abierto: boolean;
  onCerrar: () => void;
  onLeer: (codigo: string) => void;
  continuo?: boolean;
  titulo?: string;
};

export function EscanerConTelefono({ abierto, onCerrar, onLeer, continuo = false, titulo = "Escanea el código" }: Props) {
  const conectado = useTelefono((s) => s.conectado);
  const telefono = useTelefono((s) => s.telefono);
  const [leidos, setLeidos] = useState(0);
  const [ultimo, setUltimo] = useState<string | null>(null);

  // Las funciones del padre cambian en cada pintado; la captura se queda con
  // la última sin soltarse y volverse a apuntar a cada tecla.
  const alLeer = useRef(onLeer);
  alLeer.current = onLeer;
  const alCerrar = useRef(onCerrar);
  alCerrar.current = onCerrar;

  useEffect(() => {
    if (!abierto) return;
    setLeidos(0);
    setUltimo(null);
  }, [abierto]);

  useEffect(() => {
    if (!abierto || !conectado) return;
    return capturarDelTelefono((codigo) => {
      setLeidos((n) => n + 1);
      setUltimo(codigo);
      alLeer.current(codigo);
      if (!continuo) alCerrar.current();
    });
  }, [abierto, conectado, continuo]);

  if (!abierto) return null;
  if (!conectado) return <ConectarTelefono onCerrar={onCerrar} />;

  async function desconectar() {
    try {
      await soltarTelefono();
      aviso.exito("Teléfono desconectado", "Para volver a usarlo, selecciona el ícono del teléfono y escanea el nuevo código.");
      onCerrar();
    } catch (e) {
      aviso.error("No se pudo desconectar el teléfono", mensajeDeError(e));
    }
  }

  return createPortal(
    <div
      className="animar-entrada fixed bottom-4 left-4 z-[55] w-[min(20rem,calc(100vw-2rem))] rounded-[var(--radius-card)] border border-line-strong bg-surface p-4 shadow-xl"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <span className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
          <Smartphone size={20} />
          <span className="absolute right-0.5 top-0.5 size-2.5 rounded-full border-2 border-surface bg-success" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">Escaneo con el teléfono</p>
          <p className="truncate text-xs text-muted">{telefono ?? "Teléfono"} conectado · {titulo}</p>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="-mr-1 -mt-1 flex size-8 shrink-0 items-center justify-center rounded-full text-muted hover:bg-surface-hover hover:text-ink"
        >
          <X size={16} />
        </button>
      </div>

      <div className="mt-3 rounded-[var(--radius-control)] bg-surface-hover px-3 py-2 text-sm">
        {ultimo ? (
          <p className="flex items-center gap-2 font-medium text-ink">
            <Check size={15} className="shrink-0 text-success" />
            <span className="tabular min-w-0 flex-1 truncate">{ultimo}</span>
            {continuo && leidos > 1 && (
              <span className="tabular shrink-0 rounded-full bg-surface px-2 py-0.5 text-xs text-ink-soft">{leidos}</span>
            )}
          </p>
        ) : (
          <p className="flex items-center gap-2 text-ink-soft">
            <Loader2 size={15} className="shrink-0 animate-spin text-muted" />
            {continuo ? "Escanea los productos uno a uno." : "Esperando el código…"}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => void desconectar()}
        className="mt-2 text-xs text-muted underline-offset-2 hover:text-ink hover:underline"
      >
        Desconectar este teléfono
      </button>
    </div>,
    document.body,
  );
}

/**
 * La ventana del QR.
 *
 * El código vence a los diez minutos (y sirve una sola vez): se pide otro solo
 * un poco antes, para que el QR a la vista siempre funcione.
 */
function ConectarTelefono({ onCerrar }: { onCerrar: () => void }) {
  const caja = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [vuelta, setVuelta] = useState(0);
  const [listo, setListo] = useState(false);

  // Con el QR a la vista se mira más a menudo si ya llegó el teléfono.
  useEffect(() => {
    useTelefono.setState({ atento: true });
    return () => useTelefono.setState({ atento: false });
  }, []);

  useEffect(() => {
    let vivo = true;
    let renovar: number | undefined;
    setError(null);
    setListo(false);

    void (async () => {
      try {
        const [qr, { BrowserQRCodeSvgWriter }] = await Promise.all([pedirQr(), import("@zxing/browser")]);
        if (!vivo || !caja.current) return;
        const svg = new BrowserQRCodeSvgWriter().write(direccionDelQr(qr.codigo), 220, 220);
        svg.setAttribute("role", "img");
        svg.setAttribute("aria-label", "Código QR para conectar el teléfono");
        caja.current.replaceChildren(svg);
        setListo(true);
        const falta = new Date(qr.vence).getTime() - Date.now() - 30_000;
        renovar = window.setTimeout(() => setVuelta((v) => v + 1), Math.max(falta, 15_000));
      } catch (e) {
        if (vivo) setError(mensajeDeError(e));
      }
    })();

    return () => {
      vivo = false;
      window.clearTimeout(renovar);
    };
  }, [vuelta]);

  return (
    <Modal abierto onCerrar={onCerrar} titulo="Conectar teléfono" ancho="sm">
      <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-start">
        <div className="relative flex size-[236px] shrink-0 items-center justify-center rounded-[var(--radius-card)] border border-line bg-white p-2">
          <div ref={caja} className="size-[220px] [&>svg]:size-full" />
          {!listo && !error && <Loader2 size={24} className="absolute animate-spin text-neutral-400" />}
        </div>

        <div className="min-w-0 flex-1 space-y-4">
          <ol className="space-y-3 text-sm text-ink-soft">
            <Paso n={1}>Abre la cámara del teléfono.</Paso>
            <Paso n={2}>Apunta a este código y abre el enlace que aparece.</Paso>
            <Paso n={3}>Escanea los productos: cada código se recibe en esta computadora.</Paso>
          </ol>
          <p className="text-xs leading-relaxed text-muted">
            El teléfono queda vinculado solo a esta computadora. No es necesario iniciar sesión en él.
          </p>
          {error && (
            <div className="space-y-2">
              <p className="text-sm text-danger">{error}</p>
              <Boton variante="secundario" tamano="sm" onClick={() => setVuelta((v) => v + 1)}>
                Intentar de nuevo
              </Boton>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Paso({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="tabular flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent">
        {n}
      </span>
      <span className="pt-0.5">{children}</span>
    </li>
  );
}
